import { Check, Copy, RotateCcw, TimerOff } from "lucide-react";
import { useEffect, useState } from "react";

import { PayinQrCard } from "@/modules/dashboard/payins/components/PayinQrCard";
import { formatPayinRemaining, usePayinCountdown } from "@/modules/dashboard/payins/hooks/usePayinCountdown";
import { pixQrDataUrl } from "@/modules/dashboard/payins/utils/pixQrDataUrl";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";

import { PaymentSteps } from "./PaymentSteps";
import type { PaymentInstrumentProps } from "./RailDepositDrawer";
import { WaitingForPayment } from "./WaitingForPayment";

/**
 * A PIX collection ready to pay: the QR beside its copia e cola code, the time it still holds, and the three
 * steps a payer follows in any Brazilian banking app. When the QR lapses unpaid the panel says so and offers
 * a new one, instead of leaving a dead code on screen.
 */
export function PixPaymentInstrument({ result, amount, onReset }: PaymentInstrumentProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const [qrSrc, setQrSrc] = useState<string | null>(null);
  const { copiedValue, copy } = useCopyToClipboard();
  const remaining = usePayinCountdown(result.expires_at);
  const payload = result.qr_code_payload ?? "";

  // The runtime returns the EMV string, not an image; the card holds its skeleton until it is drawn.
  useEffect(() => {
    if (!payload) return;
    let cancelled = false;
    void pixQrDataUrl(payload).then((url) => {
      if (!cancelled) setQrSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [payload]);

  if (remaining !== null && remaining <= 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-muted/40 px-5 py-8 text-center">
        <TimerOff className="size-8 text-muted-foreground" aria-hidden="true" />
        <div>
          <p className="font-semibold text-foreground">{t("deposit_pix_expired_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("deposit_pix_expired_text")}</p>
        </div>
        <ColocaButton variant="primary" icon={RotateCcw} onClick={onReset}>
          {t("deposit_new")}
        </ColocaButton>
      </div>
    );
  }

  const expiresAt = result.expires_at
    ? new Date(result.expires_at).toLocaleString(activeLanguage, { dateStyle: "medium", timeStyle: "short" })
    : null;
  const copied = copiedValue === payload;

  return (
    <div className="space-y-4">
      <WaitingForPayment
        label={t("deposit_pix_waiting")}
        trailing={
          remaining !== null ? t("deposit_pix_expires_in", { time: formatPayinRemaining(remaining) }) : null
        }
        warn={remaining !== null && remaining <= 60_000}
      />
      <div className="grid gap-4 sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] sm:items-start">
        <PayinQrCard
          src={qrSrc}
          rail="PIX"
          amount={amount}
          footer={expiresAt ? `${t("deposit_expires_label")} ${expiresAt}` : undefined}
          className="sm:max-w-64"
        />
        {/* On a phone the payer is usually on this same screen and cannot scan it: the code comes first. */}
        <div className="order-first space-y-5 sm:order-none">
          {payload && (
            <section className="space-y-2.5 rounded-xl border border-border bg-card p-4">
              <h4 className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("deposit_pix_copia_label")}
              </h4>
              <p
                className="line-clamp-3 break-all font-mono text-xs leading-relaxed text-foreground"
                title={payload}
              >
                {payload}
              </p>
              <ColocaButton
                variant={copied ? "secondary" : "primary"}
                icon={copied ? Check : Copy}
                onClick={() => void copy(payload, t("deposit_pix_code_copied"))}
                className="w-full justify-center"
              >
                {copied ? t("deposit_pix_code_copied") : t("deposit_pix_copy_code")}
              </ColocaButton>
            </section>
          )}
          <PaymentSteps
            title={t("deposit_how_to_pay")}
            steps={[t("deposit_pix_step_1"), t("deposit_pix_step_2"), t("deposit_pix_step_3", { amount })]}
          />
        </div>
      </div>
    </div>
  );
}
